import { Workspace } from "@functions/state/types";
import React from "react";
import { ExpandedProject } from "./universe/types";

export function isSSR() {
    return typeof window === "undefined" || typeof window.document === "undefined";
}

function enforceObject(value: any) {
    if (value === "") {
        return undefined;
    }

    if (typeof value === undefined || value === null) {
        return value;
    }

    if (typeof value !== "object") {
        return JSON.parse(String(value));
    }

    return value;
}

function enforceString(value: any) {
    if (typeof value === undefined || value === null) {
        return value;
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    if (typeof value !== "string") {
        return String(value);
    }

    return value;
}

function enforceBoolean(value: any) {
    if (value === "") {
        return undefined;
    }

    if (typeof value === undefined || value === null) {
        return value;
    }

    if (typeof value === "string") {
        return value === "true";
    }

    return Boolean(value);
}

function enforceNumber(value: any) {
    if (value === "") {
        return undefined;
    }

    if (typeof value === undefined || value === null) {
        return value;
    }

    return Number(value);
}

const parsingMap = {
    object: enforceObject,
    string: enforceString,
    boolean: enforceBoolean,
    number: enforceNumber
};

export function parseStringifiedProps(
    component: React.FunctionComponent,
    props: Record<string, string>,
    propTypes: Record<string, string>
): React.ReactElement | null {
    if (isSSR()) {
        return component(props);
    }

    const parsedProps: Record<string, any> = {};

    for (const [key, value] of Object.entries(props)) {
        const parser = parsingMap[propTypes[key]];
        parsedProps[key] = parser ? parser(value) : value;
    }

    return component(parsedProps);
}

export function formatCompactNumber(num: number): string {
    if (num < 1000) {
        return num.toString();
    }
    if (num < 1000000) {
        const thousands = num / 1000;
        if (thousands < 10) {
            const rounded = Math.round(thousands * 100) / 100;
            return `${rounded.toString().replace(/\.?0+$/, "")}k`;
        }
        if (thousands < 100) {
            const rounded = Math.round(thousands * 10) / 10;
            return `${rounded.toString().replace(/\.0$/, "")}k`;
        }
        return `${Math.round(thousands)}k`;
    }
    const millions = num / 1000000;
    if (millions < 10) {
        const rounded = Math.round(millions * 100) / 100;
        return `${rounded.toString().replace(/\.?0+$/, "")}M`;
    }
    const rounded = Math.round(millions * 10) / 10;
    return `${rounded.toString().replace(/\.0$/, "")}M`;
}

export function getWorkspaceAvatarUrl(
    workspace: Workspace,
    projects: ExpandedProject[],
    cdnHost: string
): string | null {
    if (workspace.logo) return workspace.logo;

    const mostPopularProject = projects
        ?.filter((p) => p.icon)
        .sort((a, b) => (b.universe?.stars || 0) - (a.universe?.stars || 0))[0];

    if (mostPopularProject?.icon) {
        return `https://${cdnHost}/${workspace.id}/${mostPopularProject.icon}/original.jpg`;
    }

    return null;
}
